import React, { useState } from 'react';
import {
  Box,
  Typography,
  Divider,
  Paper,
  Button,
  Container,
  Grid,
  Alert,
  Tabs,
  Tab,
  CircularProgress
} from '@mui/material';
import { 
  Settings as SettingsIcon,
  Refresh as RefreshIcon,
  SettingsApplications as ManageIcon
} from '@mui/icons-material';
import SectionRoleManager from '../components/projects/SectionRoleManager';
import useSection from '../hooks/useSection';
import useAuth from '../hooks/useAuth';
import useNotification from '../hooks/useNotification';
import MainLayout from '../components/layout/MainLayout';

/**
 * Section Settings Page
 * Provides admin interface for managing sections and their role-based activation
 */
const SectionSettings = () => {
  const { user, hasPermission } = useAuth();
  const { sections, isLoadingSections, initializeSections, isInitializing, refetchSections } = useSection({ activeOnly: false });
  const { showNotification } = useNotification();
  
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  
  // Handle tab change
  const handleTabChange = (event, newTab) => {
    setActiveTab(newTab);
  };
  
  // Handle section refresh
  const handleRefreshSections = async () => {
    try {
      setLoading(true);
      await refetchSections();
      showNotification({
        message: 'Sections refreshed successfully',
        type: 'success'
      });
    } catch (error) {
      console.error('Error refreshing sections:', error);
      showNotification({
        message: `Failed to refresh sections: ${error.message}`,
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Handle sections initialization
  const handleInitializeSections = async () => {
    try {
      setLoading(true);
      await initializeSections();
      showNotification({
        message: 'Sections initialized successfully',
        type: 'success'
      });
    } catch (error) {
      console.error('Error initializing sections:', error);
      showNotification({
        message: `Failed to initialize sections: ${error.message}`,
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Check if user is admin or has permission to manage sections
  const hasAdminAccess = user?.role === 'admin' || hasPermission('sections', 'update');
  
  return (
    <MainLayout>
      <Box className="page-container" sx={{ py: 4, bgcolor: '#f5f6f7', minHeight: '100vh' }}>
        <Container maxWidth="xl">
          <Box className="page-title-container" sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
            <SettingsIcon sx={{ mr: 2, color: 'primary.main' }} />
            <Typography variant="h5" component="h1" fontWeight={600}>
              Section Settings
            </Typography>
          </Box>
          
          {!hasAdminAccess && (
            <Alert severity="warning" sx={{ mb: 4 }}>
              You don't have permission to manage section settings. Contact an administrator for access.
            </Alert>
          )}
          
          <Paper elevation={0} sx={{ mb: 4, borderRadius: 2 }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label="Section Role Management" icon={<ManageIcon />} iconPosition="start" />
            </Tabs>
            
            <Box sx={{ p: 3 }}>
              {activeTab === 0 && (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Manage Section Roles and Activation
                    </Typography>
                    
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={handleRefreshSections}
                        disabled={loading || isLoadingSections || !hasAdminAccess}
                      >
                        Refresh
                      </Button>
                      
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={handleInitializeSections}
                        disabled={loading || isInitializing || !hasAdminAccess}
                        sx={{ 
                          bgcolor: '#d35b9e',
                          '&:hover': {
                            bgcolor: '#b9407e'
                          }
                        }}
                      >
                        {isInitializing ? <CircularProgress size={24} /> : 'Initialize Sections'}
                      </Button>
                    </Box>
                  </Box>
                  
                  <Divider sx={{ mb: 3 }} />
                  
                  <SectionRoleManager onUpdate={handleRefreshSections} />
                </Box>
              )}
            </Box>
          </Paper>
          
          <Paper elevation={0} sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>
              About Section Management
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>
                  Section Role-Based Access Control
                </Typography>
                <Typography variant="body2" paragraph>
                  In this interface, administrators can control which user roles have access to different sections
                  of the project creation workflow. When a section is activated for a specific role, users with that
                  role will see the section and its tasks during project creation.
                </Typography>
                <Typography variant="body2">
                  To modify section access permissions:
                </Typography>
                <ol>
                  <li>Toggle the "Active" switch to activate or deactivate a section</li>
                  <li>Check the role checkboxes to grant that role access to the section</li>
                  <li>Click "Save Settings" to apply your changes</li>
                </ol>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>
                  Section Initialization
                </Typography>
                <Typography variant="body2" paragraph>
                  If you need to reset or re-initialize the default sections and tasks, use the "Initialize Sections"
                  button. This will create the standard three sections (Functional Analysis, Design, Technical) with their
                  default tasks.
                </Typography>
                <Alert severity="info" sx={{ mt: 2 }}>
                  Note: Initialization will not delete existing sections, but will add new ones if they don't exist.
                  Use this function if the sections are missing or incomplete.
                </Alert>
              </Grid>
            </Grid>
          </Paper>
        </Container>
      </Box>
    </MainLayout>
  );
};

export default SectionSettings;